import { useState } from 'react';

import { CiEdit } from "react-icons/ci";
import { MdDeleteSweep } from "react-icons/md";
import { formatDate } from '../../utils/Utils';
import { BeatLoader } from "react-spinners";
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Swal from 'sweetalert2'
import { useDeleteOperationJourney, useFetchOperationJourney } from '../../hooks/useOperationJourney';
import OperationJourneyModal from '../OperationJourney/OperationJourneyModal';
const OperationJourneyTable = ({ industriesType, filteredData }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [expandedOperations, setExpandedOperations] = useState({});
  const { data, error, isLoading } = useFetchOperationJourney(industriesType, filteredData);
  const [isExpanded, setIsExpanded] = useState({});
  const toggleDescription = (operationJourneyId) => {
    setIsExpanded(prev => ({ ...prev, [operationJourneyId]: !prev[operationJourneyId] }));
  };
  const deleteOperationJourneyMutation = useDeleteOperationJourney();
  const handleEdit = (operation) => {
    setSelectedOperation(operation);
    setShowPopup(true);
  };
  const handleClose = () => {
    setShowPopup(false);
  };
  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to undo this action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          deleteOperationJourneyMutation.mutate(id);
          Swal.fire({
            title: "Removed!",
            text: "The operation journey has been successfully removed.",
            icon: "success",
          });
        } catch (error) {
          Swal.fire({
            title: "Error!",
            text: "An unexpected error occurred. Please try again later.",
            icon: "error",
          });
        }
      }
    });
  };

  const toggleSteps = (operationId) => {
    setExpandedOperations((prev) => ({
      ...prev,
      [operationId]: !prev[operationId],
    }));
  };

  if (isLoading) return <div className='col-span-full flex justify-center items-center'><BeatLoader /></div>;
  if (error) return <div className='col-span-full flex justify-center items-center'>Error loading Operation Journey</div>;

  return (
    <div className="col-span-full rounded-xl shadow-xl border border-slate-200 bg-gradient-to-br from-white via-gray-50 to-white">
      <header className="px-6 py-5 border-b border-slate-200 bg-white rounded-t-xl">
        <h2 className="text-xl font-semibold text-slate-800">Operation Journey</h2>
      </header>

      <div className="p-6">
        <div className="overflow-x-auto rounded-lg">
          <table className="min-w-full text-sm text-slate-700 bg-white rounded-lg">
            <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="p-4 text-left">Name</th>
                <th className="p-4 text-left">Industry</th>
                <th className="p-4 text-left">Description</th>
                <th className="p-4 text-left">Steps</th>
                <th className="p-4 text-left">Creation Date</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {data?.map(operation => (
                <tr key={operation.id} className="hover:bg-slate-50 transition duration-200">
                  <td className="p-4 max-w-[20%] font-medium text-slate-800">
                    {operation?.name
                      ? operation.name.charAt(0).toUpperCase() + operation.name.slice(1).toLowerCase()
                      : ''}
                  </td>

                  <td className="p-4">
                    <span className="inline-block px-2 py-1 text-sm font-semibold text-blue-700 rounded-full">
                      {operation?.industryDetails.name
                        ? operation.industryDetails.name.charAt(0).toUpperCase() + operation.industryDetails.name.slice(1)
                        : ''}
                    </span>
                  </td>

                  <td className="p-4 max-w-[20%] whitespace-normal text-slate-600">
                    <p className={isExpanded[operation.id] ? '' : 'line-clamp-3'}>
                      {operation?.description}
                    </p>
                    <button
                      onClick={()=>toggleDescription(operation.id)}
                      className="font-medium text-slate-800 mt-2"
                    >
                      {isExpanded[operation.id] ? 'View less' : 'View more'}
                    </button>
                  </td>

                  <td className="p-4 max-w-[25%] whitespace-normal">
                    <ul className="flex flex-wrap gap-2">
                      {operation?.steps
                        .slice(0, expandedOperations[operation.id] ? operation?.touchpointCJBP?.steps?.length : 2)
                        .map((item, index) => (
                          <li
                            key={index}
                            className=" text-green-800 text-sm font-medium px-2.5 py-0.5 rounded-full"
                          >
                            {item.charAt(0).toUpperCase() + item.slice(1)}
                          </li>
                        ))}
                    </ul>

                    <button
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center transition"
                      onClick={() => toggleSteps(operation.id)}
                    >
                      {expandedOperations[operation.id] ? <FaChevronUp /> : <FaChevronDown />}

                    </button>
                  </td>

                  <td className="p-4 text-slate-500">
                    {!operation.createdAt ? "" : formatDate(operation.createdAt)}
                  </td>

                  <td className="p-4 text-center">
                    <div className="flex justify-center items-center gap-4">
                      <button
                        onClick={() => handleEdit(operation)}
                        title="Edit"
                        className="transition-transform duration-150 hover:scale-110"
                      >
                        <CiEdit className="w-6 h-6 text-green-600" />
                      </button>

                      <button
                        onClick={() => handleDelete(operation.id)}
                        title="Delete"
                        className="transition-transform duration-150 hover:scale-110"
                      >
                        <MdDeleteSweep className="w-6 h-6 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showPopup && (
          <OperationJourneyModal 
            mode="update" 
            onClose={handleClose}
            selectedOperation={selectedOperation}
        />
        )}
      </div>
    </div>

  );
}

export default OperationJourneyTable;
